import React, { useEffect, useMemo, useState } from "react";
import { PHONE_COUNTRIES, DEFAULT_DIAL, parsePhone, formatPhone } from "@/lib/phoneCountries";
import { CountryCombobox } from "@/components/CountryCombobox";

/**
 * Reusable phone input with country-code dropdown + digits-only local number.
 * Stores value as a single string ("+966 555123456") so backend stays unchanged.
 * Defaults to Saudi Arabia (+966) when value is empty.
 *
 * The dial code is kept as internal state so that a user can change the
 * country BEFORE typing any digits without the parent-derived value silently
 * reverting to the default (formatPhone returns "" when local is empty,
 * which would otherwise re-parse to DEFAULT_DIAL on the next render).
 */
export default function PhoneInput({
  value,
  onChange,
  testId,
  optional = false,
  placeholder = "555 123 456",
  className = "",
}) {
  // Derive dial + local from the incoming value.
  const parsed = useMemo(() => parsePhone(value), [value]);
  const [dial, setDial] = useState(parsed.dial);

  // Keep internal dial in sync when the parent value carries an explicit dial.
  // We only overwrite our local dial if the parsed value actually contains
  // digits (i.e. a real number is stored) — otherwise the user's chosen dial
  // survives even while the digits field is empty.
  useEffect(() => {
    if (parsed.local) setDial(parsed.dial);
  }, [parsed.dial, parsed.local]);

  const local = parsed.local;
  const selectedCode = useMemo(
    () => (PHONE_COUNTRIES.find((c) => c.dial === dial)?.code) || "",
    [dial],
  );

  const setDialByCode = (code) => {
    const c = PHONE_COUNTRIES.find((x) => x.code === code);
    if (!c) return;
    setDial(c.dial);
    // If there are already digits, propagate an updated formatted value so the
    // parent's state matches the visible trigger. If not, keep parent value as
    // empty string until the user types digits — parsePhone("") falls back to
    // DEFAULT_DIAL but our internal state overrides that until digits appear.
    if (local) onChange(formatPhone(c.dial, local));
  };
  const setLocal = (raw) => {
    const digits = String(raw || "").replace(/[^0-9]/g, "");
    onChange(formatPhone(dial, digits));
  };

  return (
    <div className={`flex items-stretch gap-1.5 ${className}`} data-testid={testId ? `${testId}-wrapper` : undefined}>
      <div className="w-[110px] flex-shrink-0">
        <CountryCombobox
          options={PHONE_COUNTRIES}
          value={selectedCode}
          onChange={setDialByCode}
          valueField="code"
          showDial
          compact
          testId={testId ? `${testId}-dial` : "phone-dial"}
          placeholder="+…"
        />
      </div>
      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        required={!optional}
        value={local}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key.length > 1 || e.ctrlKey || e.metaKey || e.altKey) return;
          if (!/[0-9]/.test(e.key)) e.preventDefault();
        }}
        onChange={(e) => setLocal(e.target.value)}
        onPaste={(e) => {
          e.preventDefault();
          const text = (e.clipboardData || window.clipboardData).getData("text");
          setLocal(text);
        }}
        data-testid={testId}
        className="flex-1 min-w-0 border border-[#e2e8f0] bg-white text-[#0f1b2d] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 transition"
      />
    </div>
  );
}

export { DEFAULT_DIAL };
